import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getSubAppPortalContainer } from "@eweadn-qiankun/shared-utils";
import styles from "./Tooltip.module.css";
import { TooltipProps } from "./types";

/** 气泡小三角（原 ::after，改为 span 以兼容 qiankun 样式隔离） */
function TooltipArrow({
  placement,
  fill = "var(--bg-tertiary)",
}: {
  placement: "top" | "bottom" | "right" | "left";
  /** 与气泡背景一致，默认与 --overlay-panel-bg 一致 */
  fill?: string;
}) {
  const base: React.CSSProperties = {
    position: "absolute",
    width: 0,
    height: 0,
    pointerEvents: "none",
    zIndex: 1,
  };

  if (placement === "right") {
    // 气泡在锚点右侧：三角在气泡左边缘，朝左指向锚点（borderRight 着色 → 三角朝左）
    return (
      <span
        aria-hidden
        style={{
          ...base,
          left: "calc(0px - var(--tooltip-arrow-height, 8px) + 1px)",
          top: "50%",
          transform: "translateY(-50%)",
          borderTop: "var(--tooltip-arrow-size, 6px) solid transparent",
          borderBottom: "var(--tooltip-arrow-size, 6px) solid transparent",
          borderRight: `var(--tooltip-arrow-height, 8px) solid ${fill}`,
        }}
      />
    );
  }

  if (placement === "left") {
    // 气泡在锚点左侧：三角在气泡右边缘，朝右指向锚点（borderLeft 着色 → 三角朝右）
    return (
      <span
        aria-hidden
        style={{
          ...base,
          right: "calc(0px - var(--tooltip-arrow-height, 8px) + 1px)",
          top: "50%",
          transform: "translateY(-50%)",
          borderTop: "var(--tooltip-arrow-size, 6px) solid transparent",
          borderBottom: "var(--tooltip-arrow-size, 6px) solid transparent",
          borderLeft: `var(--tooltip-arrow-height, 8px) solid ${fill}`,
        }}
      />
    );
  }

  if (placement === "top") {
    // 气泡在锚点上方：三角在气泡底边，朝下指向锚点
    return (
      <span
        aria-hidden
        style={{
          ...base,
          left: "50%",
          transform: "translateX(-50%)",
          top: "calc(100% - 1px)",
          borderLeft: "var(--tooltip-arrow-size, 6px) solid transparent",
          borderRight: "var(--tooltip-arrow-size, 6px) solid transparent",
          borderTop: `var(--tooltip-arrow-height, 8px) solid ${fill}`,
        }}
      />
    );
  }

  // bottom: 气泡在锚点下方，三角在气泡顶边，朝上指向锚点
  return (
    <span
      aria-hidden
      style={{
        ...base,
        left: "50%",
        transform: "translateX(-50%)",
        bottom: "calc(100% - 1px)",
        borderLeft: "var(--tooltip-arrow-size, 6px) solid transparent",
        borderRight: "var(--tooltip-arrow-size, 6px) solid transparent",
        borderBottom: `var(--tooltip-arrow-height, 8px) solid ${fill}`,
      }}
    />
  );
}

export const Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  className = "",
  delay = 100,
  trigger = "hover",
  placement = "bottom",
  usePortal = false,
  portalGapPx = 12,
  gap,
  portalOffsetX = 0,
  open = true,
  portalScrollParentSelector,
  getAnchorElement,
  getPortalContainer,
  overlayClassName = "",
  overlayProps,
  maxWidth,
  zIndex,
  bgColor = "var(--overlay-panel-bg)",
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [portalMountNode, setPortalMountNode] = useState<HTMLElement | null>(
    null
  );
  const [portalCoords, setPortalCoords] = useState<{
    left: number;
    top: number;
    ready: boolean;
  }>({ left: 0, top: 0, ready: false });

  // 用 ref 存可变 props，避免 update 闭包过期、也避免 effect 依赖 text 频繁重建监听
  const placementRef = useRef(placement);
  placementRef.current = placement;
  const portalGapPxRef = useRef(portalGapPx);
  portalGapPxRef.current = portalGapPx;
  const gapRef = useRef(gap);
  gapRef.current = gap;
  const portalOffsetXRef = useRef(portalOffsetX);
  portalOffsetXRef.current = portalOffsetX;
  const portalScrollParentSelectorRef = useRef(portalScrollParentSelector);
  portalScrollParentSelectorRef.current = portalScrollParentSelector;
  const getAnchorElementRef = useRef(getAnchorElement);
  getAnchorElementRef.current = getAnchorElement;

  const resolvePortalContainer = useCallback(
    (anchorEl: HTMLElement): HTMLElement => {
      if (getPortalContainer) {
        return getPortalContainer(anchorEl);
      }
      return typeof document !== "undefined"
        ? getSubAppPortalContainer()
        : anchorEl;
    },
    [getPortalContainer]
  );

  const isHover = trigger === "hover";
  const isAlways = trigger === "always";
  const [isVisible, setIsVisible] = useState(false);
  const timerRef = useRef<number | null>(null);
  const showBubble = isAlways ? open : isVisible;

  const handleMouseEnter = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  useLayoutEffect(() => {
    if (!usePortal || !showBubble) {
      setPortalMountNode(null);
      setPortalCoords((c) => ({ ...c, ready: false }));
      return;
    }

    const el = wrapperRef.current;
    if (!el) {
      setPortalMountNode(null);
      setPortalCoords((c) => ({ ...c, ready: false }));
      return;
    }

    setPortalMountNode(resolvePortalContainer(el));

    const resolveAnchor = () => getAnchorElementRef.current?.(el) ?? el;

    const update = () => {
      const anchorEl = resolveAnchor();
      const r = anchorEl.getBoundingClientRect();
      const p = placementRef.current;
      const gapVal = gapRef.current ?? portalGapPxRef.current;
      const offsetX = portalOffsetXRef.current;
      let left: number;
      let top: number;

      if (p === "right") {
        left = r.right + gapVal;
        top = r.top + r.height / 2;
      } else if (p === "left") {
        left = r.left - gapVal;
        top = r.top + r.height / 2;
      } else if (p === "top") {
        left = r.left + r.width / 2 + offsetX;
        top = r.top - gapVal;
      } else {
        left = r.left + r.width / 2 + offsetX;
        top = r.bottom + gapVal;
      }

      setPortalCoords((prev) => {
        if (
          prev.ready &&
          Math.abs(prev.left - left) < 0.5 &&
          Math.abs(prev.top - top) < 0.5
        ) {
          return prev;
        }
        return { left, top, ready: true };
      });
    };

    update();

    let scrollEl: Element | null = null;
    const scrollSelector = portalScrollParentSelectorRef.current;
    if (scrollSelector) {
      scrollEl = el.closest(scrollSelector);
    }
    scrollEl?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(resolveAnchor());
    }

    return () => {
      scrollEl?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, [usePortal, showBubble, resolvePortalContainer]);

  const wrapperClass = [
    styles.tooltipWrapper,
    "eweadn-tooltip-trigger",
    !isHover && showBubble && !usePortal ? styles.tooltipWrapperAlways : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const tooltipClass = [
    styles.tooltip,
    placement === "top" ? styles.tooltipTop : "",
    placement === "right" ? styles.tooltipRight : "",
    placement === "left" ? styles.tooltipLeft : "",
  ]
    .filter(Boolean)
    .join(" ");

  const portalClass = [styles.tooltipPortal, overlayClassName]
    .filter(Boolean)
    .join(" ");

  const effectiveGap = gap ?? portalGapPx;

  const bubbleMaxStyle: React.CSSProperties =
    maxWidth != null
      ? {
          maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
        }
      : {};

  if (isAlways && !open) {
    return (
      <div ref={wrapperRef} className={wrapperClass}>
        {children}
      </div>
    );
  }

  /**
   * Portal 层定位：视觉样式统一由 Tooltip.module.css 的 `.tooltipPortal` 提供，
   * 这里仅保留跟随锚点变化的定位信息，避免在 TSX 中写外观样式。
   */
  const portalLayerStyle: React.CSSProperties = {
    left: portalCoords.left,
    top: portalCoords.top,
    ...(placement === "right"
      ? { transform: "translate(0, -50%)" }
      : placement === "left"
        ? { transform: "translate(-100%, -50%)" }
        : placement === "top"
          ? { transform: "translate(-50%, -100%)" }
          : { transform: "translate(-50%, 0)" }),
    visibility: portalCoords.ready ? "visible" : "hidden",
    ...bubbleMaxStyle,
  };

  const portalNode =
    usePortal &&
    showBubble &&
    typeof document !== "undefined" &&
    portalMountNode
      ? createPortal(
          <div
            className={portalClass}
            data-eweadn-tooltip-portal=""
            data-placement={placement}
            style={{
              ...portalLayerStyle,
              background: bgColor,
              ...(maxWidth !== undefined ? { maxWidth } : {}),
              ...(zIndex !== undefined ? { zIndex } : {}),
            }}
            {...overlayProps}
          >
            <TooltipArrow placement={placement} fill={bgColor} />
            {text}
          </div>,
          portalMountNode
        )
      : null;

  if (usePortal) {
    return (
      <>
        <div
          ref={wrapperRef}
          className={wrapperClass}
          onMouseEnter={isHover ? handleMouseEnter : undefined}
          onMouseLeave={isHover ? handleMouseLeave : undefined}
        >
          {children}
        </div>
        {portalNode}
      </>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={wrapperClass}
      onMouseEnter={isHover ? handleMouseEnter : undefined}
      onMouseLeave={isHover ? handleMouseLeave : undefined}
    >
      {children}
      {showBubble ? (
        <div
          className={tooltipClass}
          data-eweadn-tooltip-bubble=""
          data-placement={placement}
          style={
            {
              background: bgColor,
              "--tooltip-gap": `${effectiveGap}px`,
              transitionDelay: isHover ? `${delay}ms` : undefined,
              ...(maxWidth !== undefined ? { maxWidth } : {}),
              ...(zIndex !== undefined ? { zIndex } : {}),
            } as React.CSSProperties
          }
        >
          <TooltipArrow placement={placement} fill={bgColor} />
          {text}
        </div>
      ) : null}
    </div>
  );
};
